import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  isDevMode,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import type { BzIconDef } from './bz-icon-def';
import { BzIconRegistry } from './bz-icon-registry';

export type BzIconSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Renders one icon from `@bringoz/ui/icons`.
 *
 * Two ways to say which icon, and the choice matters:
 *
 *   <bz-icon [icon]="bzIconTruck" />   fixed icon — type-checked, tree-shaken,
 *                                      needs no registration
 *   <bz-icon [name]="row.status" />    name only known at runtime — resolved
 *                                      through provideBzIcons()
 *
 * Colour comes from CSS `color`, because single-colour icons are generated
 * with `currentColor`. Size comes from the `size` input or from overriding
 * `--bz-icon-size` on the element.
 */
@Component({
  selector: 'bz-icon',
  template: '',
  styleUrl: './bz-icon.scss',
  host: {
    '[class]': 'hostClass()',
    '[innerHTML]': 'svg()',
    '[attr.role]': 'label() ? "img" : null',
    '[attr.aria-label]': 'label() ?? null',
    '[attr.aria-hidden]': 'label() ? null : "true"',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzIcon {
  /** The icon itself. Prefer this over `name` whenever the icon is fixed. */
  readonly icon = input<BzIconDef | undefined>(undefined);

  /** Registry lookup key. For names that are only known at runtime. */
  readonly name = input<string | undefined>(undefined);

  readonly size = input<BzIconSize>('lg');

  /**
   * Accessible name. Leave unset for a decorative icon sitting next to text —
   * the host is then marked aria-hidden so a screen reader does not announce
   * the same thing twice.
   */
  readonly label = input<string | undefined>(undefined);

  private readonly registry = inject(BzIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly hostClass = computed(() => `bz-icon bz-icon--${this.size()}`);

  private readonly resolved = computed(() => {
    const direct = this.icon();
    if (direct) return direct;

    const name = this.name();
    if (!name) return undefined;

    const found = this.registry.get(name);
    if (!found && isDevMode()) {
      console.warn(
        `[bz-icon] "${name}" is not registered. Pass it to provideBzIcons(), ` +
          `or bind the icon directly with [icon].`,
      );
    }
    return found;
  });

  // The markup is generated at build time from our own SVG files, never from
  // application data, so there is no untrusted input for the sanitizer to
  // guard against — and it would strip the SVG if asked to.
  protected readonly svg = computed(() => {
    const def = this.resolved();
    if (!def) return '';

    return this.sanitizer.bypassSecurityTrustHtml(
      `<svg viewBox="${def.viewBox}" fill="${def.fill ?? 'currentColor'}" ` +
        `xmlns="http://www.w3.org/2000/svg" focusable="false">${def.body}</svg>`,
    );
  });
}
